import { useEffect, useState, useContext } from "react";
import { Card, Typography, Radio, Divider, Spin, Button, Tabs } from "antd";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../context/AuthContext";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export default function ShowOneAssessment({ selectedData }) {
  const { authData, backUrl } = useContext(AuthContext);
  const [displayedAnswers, setDisplayedAnswers] = useState([]);
  const [previousReviews, setPreviousReviews] = useState([]);
  const [updatedAnswers, setUpdatedAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [newAnswers, setNewAnswers] = useState({});
  const [activeTab, setActiveTab] = useState("First Response");
  const [notEditable, setNotEditable] = useState(false);

  // set notEditable based on role
  useEffect(() => {
    if (authData?.employee?.role === 2) {
      setNotEditable(true);
    } else {
      setNotEditable(false);
    }
  }, [authData]);

  // initialize data when selectedData changes
  useEffect(() => {
    if (selectedData) {
      const employeeAns = selectedData.appraisal?.employee_answers || [];
      const reviews = selectedData.previous_reviews || [];

      // If reviewer_answers exist, treat it as current user's review
      if (selectedData.reviewer_answers && selectedData.reviewer_answers.length > 0) {
        const yourReview = {
          approver_name: authData?.user?.name || "Your Review",
          answers: selectedData.reviewer_answers.map((ans) => ({
            id: ans.id,
            chosen_option: ans.chosen_option,
            question: {
              ...ans.question,
              options: ans.question?.options || [],
              section: ans.question?.section || "General",
              order: ans.question?.order,
              text: ans.question?.text,
            },
          })),
        };

        // ✅ Add only if not already present
        const alreadyExists = reviews.some(
          (r) => r.approver_name === (authData?.user?.name || "Your Review")
        );

        if (!alreadyExists) {
          reviews.push(yourReview);
        }

        setActiveTab(authData?.user?.name || "Your Review");
      } else {
        setActiveTab("First Response");
      }


      setPreviousReviews(reviews);
      setDisplayedAnswers(employeeAns);

      // updatedAnswers keyed by answer.id
      const defaults = {};
      employeeAns.forEach((a) => {
        defaults[a.id] = a.chosen_option || null;
      });
      setUpdatedAnswers(defaults);

      setNewAnswers({});
    }

  }, [selectedData]);

  // handle tab changes
  const handleTabChange = (key) => {
    setActiveTab(key);

    if (key === "First Response") {
      const employeeAns = selectedData.appraisal?.employee_answers || [];
      setDisplayedAnswers(employeeAns);
      const defaults = {};
      employeeAns.forEach((a) => (defaults[a.id] = a.chosen_option || null));
      setUpdatedAnswers(defaults);
    } else if (key === "New Questions") {
      // For new questions, reuse grouped questions
    } else {
      const foundReview = previousReviews.find((r) => r.approver_name === key);
      if (foundReview) {
        const answers = foundReview.answers || [];
        setDisplayedAnswers(answers);
        const defaults = {};
        answers.forEach((a) => (defaults[a.id] = a.chosen_option || null));
        setUpdatedAnswers(defaults);
      }
    }
  };

  const handleSelect = (answerId, value) => {
    if (notEditable) return;
    setUpdatedAnswers((prev) => ({ ...prev, [answerId]: value }));
  };

  const handleNewSelect = (questionId, optionId) => {
    if (notEditable) return;
    setNewAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  // Submit existing answers (First Response)
  const handleSubmit = async () => {
    if (notEditable) return;

    const payload = {
      answers: displayedAnswers.map((item) => ({
        answer_id: item.id,
        chosen_option_id: updatedAnswers[item.id] ?? null,
        remarks: "",
      })),
    };

    try {
      setLoading(true);
      await axios.post(
        `${backUrl}/api/appraisal/appraisal-tasks/${selectedData.id}/submit-review/`,
        payload,
        {
          headers: { Authorization: `Bearer ${authData?.access}` },
        }
      );

      toast.success("Assessment submitted successfully!");

      if (!notEditable) {
        const newReview = {
          approver_name: authData?.user?.name || "Your Review",
          answers: displayedAnswers.map((item) => ({
            id: item.id,
            chosen_option: updatedAnswers[item.id] ?? null,
            question: {
              ...item.question,
              options: item.question?.options || [],
              section: item.question?.section || "General",
              order: item.question?.order,
              text: item.question?.text,
            },
          })),
        };

        setPreviousReviews((prev) => {
          const alreadyExists = prev.some(
            (r) => r.approver_name === (authData?.user?.name || "Your Review")
          );
          return alreadyExists ? prev : [...prev, newReview];
        });

        setActiveTab(authData?.user?.name || "Your Review");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit assessment.");
    } finally {
      setLoading(false);
    }
  };

  // Submit new answers (New Questions)
  const handleSubmitNew = async () => {
    if (notEditable) return;

    const answersPayload = Object.keys(newAnswers).map((qid) => ({
      answer_id: parseInt(qid, 10),
      chosen_option_id: newAnswers[qid],
      remarks: "",
    }));

    if (answersPayload.length === 0) {
      toast.info("Please select options before submitting new answers.");
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${backUrl}/api/appraisal/appraisal-tasks/${selectedData.id}/submit-review/`,
        { answers: answersPayload },
        {
          headers: { Authorization: `Bearer ${authData?.access}` },
        }
      );

      toast.success("New assessment submitted successfully!");

      if (!notEditable) {
        const newReview = {
          approver_name: authData?.user?.name || "Your Review",
          answers: Object.keys(newAnswers).map((qid) => {
            const foundQ = displayedAnswers.find(
              (a) => a.question?.id === parseInt(qid, 10)
            );
            return {
              id: parseInt(qid, 10),
              chosen_option: newAnswers[qid],
              question: foundQ
                ? {
                  ...foundQ.question,
                  options: foundQ.question?.options || [],
                  section: foundQ.question?.section || "General",
                  order: foundQ.question?.order,
                  text: foundQ.question?.text,
                }
                : { id: parseInt(qid, 10), text: "Question unavailable" },
            };
          }),
        };

        setPreviousReviews((prev) => {
          const alreadyExists = prev.some(
            (r) => r.approver_name === (authData?.user?.name || "Your Review")
          );
          return alreadyExists ? prev : [...prev, newReview];
        });

        setActiveTab(authData?.user?.name || "Your Review");
        setNewAnswers({});
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit new assessment.");
    } finally {
      setLoading(false);
    }
  };

  if (!selectedData) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayedAnswers.length && activeTab !== "New Questions") {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <Text type="secondary">No answers available</Text>
      </div>
    );
  }

  const grouped = displayedAnswers.reduce((acc, item) => {
    const section = item.question?.section || "General";
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  return (
    <div style={{ padding: "20px 40px" }}>
      <Title level={3} style={{ textAlign: "center", marginBottom: 20 }}>
        Appraisal Assessment
      </Title>

      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="First Response" key="First Response">
          {Object.keys(grouped).map((section, idx) => (
            <Card
              key={idx}
              title={<Text strong>{section}</Text>}
              style={{
                marginBottom: 20,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {grouped[section].map((item) => (
                <div key={item.id} style={{ marginBottom: 20 }}>
                  <Text style={{ fontWeight: 500 }}>
                    {item.question?.order}. {item.question?.text}
                  </Text>
                  <div style={{ marginTop: 10 }}>
                    <Radio.Group
                      value={updatedAnswers[item.id]}
                      onChange={(e) => handleSelect(item.id, e.target.value)}
                      disabled={notEditable || activeTab !== "First Response"}
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexWrap: "wrap",
                        marginTop: "8px",
                      }}
                    >
                      {item.question?.options?.length ? (
                        item.question.options.map((opt) => (
                          <Radio key={opt.id} value={opt.id}>
                            {opt.text}
                          </Radio>
                        ))
                      ) : (
                        <Text type="secondary">No options available</Text>
                      )}
                    </Radio.Group>
                  </div>
                </div>
              ))}
            </Card>
          ))}

          {!notEditable && (
            <>
              <Divider />
              <div style={{ textAlign: "center", marginTop: 20 }}>
                <Button
                  type="primary"
                  size="large"
                  loading={loading}
                  onClick={handleSubmit}
                >
                  Submit Assessment
                </Button>
              </div>
            </>
          )}
        </TabPane>

        {previousReviews.map((r) => (
          <TabPane tab={r.approver_name} key={r.approver_name}>
            {r.answers && r.answers.length > 0 ? (
              r.answers.map((ans) => (
                <Card
                  key={ans.id}
                  title={<Text strong>{ans.question?.section || "General"}</Text>}
                  style={{
                    marginBottom: 20,
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <Text style={{ fontWeight: 500 }}>
                    {ans.question?.order}. {ans.question?.text}
                  </Text>
                  <div style={{ marginTop: 10 }}>
                    <Radio.Group
                      value={ans.chosen_option}
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexWrap: "wrap",
                        marginTop: "8px",
                        pointerEvents: "none",
                        opacity: 1,
                      }}
                    >
                      {ans.question?.options?.map((opt) => (
                        <Radio key={opt.id} value={opt.id}>
                          {opt.text}
                        </Radio>
                      ))}
                    </Radio.Group>
                  </div>
                </Card>
              ))
            ) : (
              <Text type="secondary">
                No answers available for this reviewer.
              </Text>
            )}
          </TabPane>
        ))}

        <TabPane tab="New Questions" key="New Questions">
          {Object.keys(grouped).map((section, idx) => (
            <Card
              key={idx}
              title={<Text strong>{section}</Text>}
              style={{
                marginBottom: 20,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {grouped[section].map((item) => (
                <div key={item.id} style={{ marginBottom: 20 }}>
                  <Text style={{ fontWeight: 500 }}>
                    {item.question?.order}. {item.question?.text}
                  </Text>
                  <div style={{ marginTop: 10 }}>
                    <Radio.Group
                      value={newAnswers[item.question?.id]}
                      onChange={(e) =>
                        handleNewSelect(item.question?.id, e.target.value)
                      }
                      disabled={notEditable}
                      style={{
                        display: "flex",
                        gap: "25px",
                        flexWrap: "wrap",
                        marginTop: "8px",
                      }}
                    >
                      {item.question?.options?.length ? (
                        item.question.options.map((opt) => (
                          <Radio key={opt.id} value={opt.id}>
                            {opt.text}
                          </Radio>
                        ))
                      ) : (
                        <Text type="secondary">No options available</Text>
                      )}
                    </Radio.Group>
                  </div>
                </div>
              ))}
            </Card>
          ))}

          {!notEditable && (
            <>
              <Divider />
              <div style={{ textAlign: "center", marginTop: 20 }}>
                <Button
                  type="primary"
                  size="large"
                  loading={loading}
                  onClick={handleSubmitNew}
                >
                  Submit New Answers
                </Button>
              </div>
            </>
          )}
        </TabPane>
      </Tabs>

      <ToastContainer />
    </div>
  );
}
